import { useMutation, useQuery } from '@tanstack/react-query';
import { Eye, EyeOff } from 'lucide-react';
import { useState, type ChangeEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ApiError, api, tokens } from '../lib/api';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { FormPage } from '../components/FormPage';
import { Button, Field, Input, useToast } from '../components/ui';

type FieldName = 'current' | 'next' | 'again';

/**
 * Route: `/account/password` — anyone changes their own password (operator,
 * 2026-10-06: "it must let me and anyone change his password"). One password
 * signs in to the admin and to Finance, so a change signs every other device,
 * and the other system, out; this page carries on with the fresh tokens the
 * API hands back.
 */
export function ChangePasswordPage() {
  const { t } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState<Record<FieldName, string>>({ current: '', next: '', again: '' });
  const [show, setShow] = useState(false);
  // Kept as a key, not text, so it follows the language when it is switched.
  const [problem, setProblem] = useState<{ field: FieldName; key: TranslationKey; vars?: Record<string, number> } | null>(null);

  // Settings › Security sets the minimum; the API holds to at least 8 whatever it says.
  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get<Record<string, Record<string, unknown>>>('/settings'),
  });
  const minLength = Math.max(8, Number(settings.data?.security?.['security.passwordMinLength']) || 8);

  const change = useMutation({
    mutationFn: () =>
      api.post<{ accessToken: string; refreshToken: string }>('/auth/change-password', {
        currentPassword: form.current,
        newPassword: form.next,
      }),
    onSuccess: (fresh) => {
      tokens.set(fresh.accessToken, fresh.refreshToken);
      toast.success(t('auth.passwordChanged'));
      // Back to where they came from; a page opened on its own goes home.
      if (location.key !== 'default') navigate(-1);
      else navigate('/', { replace: true });
    },
    onError: (e: Error) => {
      if (e instanceof ApiError && e.code === 'INVALID_CREDENTIALS') {
        setProblem({ field: 'current', key: 'auth.currentPasswordWrong' });
      } else if (e instanceof ApiError && e.code === 'VALIDATION_FAILED' && (e.details as any)?.minLength) {
        setProblem({ field: 'next', key: 'auth.passwordTooShort', vars: { n: (e.details as any).minLength } });
      } else {
        toast.error(e.message);
      }
    },
  });

  const submit = () => {
    if (form.next.length < minLength) {
      setProblem({ field: 'next', key: 'auth.passwordTooShort', vars: { n: minLength } });
    } else if (form.next !== form.again) {
      setProblem({ field: 'again', key: 'auth.passwordMismatch' });
    } else {
      setProblem(null);
      change.mutate();
    }
  };

  const set = (field: FieldName) => (e: ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [field]: e.target.value });
    if (problem?.field === field) setProblem(null);
  };
  const errorFor = (field: FieldName) => (problem?.field === field ? t(problem.key, problem.vars) : null);

  return (
    <FormPage
      title={t('auth.changePassword')}
      subtitle={t('auth.changePasswordSubtitle')}
      backLabel={t('common.back')}
      submitLabel={t('auth.changePassword')}
      onSubmit={submit}
      submitting={change.isPending}
      disabled={!form.current || !form.next || !form.again}
    >
      <div className="max-w-md space-y-4">
        <Field label={t('auth.currentPassword')} required error={errorFor('current')}>
          <Input
            type="password"
            dir="ltr"
            autoComplete="current-password"
            value={form.current}
            onChange={set('current')}
            autoFocus
          />
        </Field>
        <Field label={t('auth.newPassword')} required hint={t('auth.passwordHint', { n: minLength })} error={errorFor('next')}>
          <div className="flex gap-2">
            <Input
              type={show ? 'text' : 'password'}
              dir="ltr"
              autoComplete="new-password"
              value={form.next}
              onChange={set('next')}
              className="min-w-0 flex-1"
            />
            <Button
              icon={show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              aria-label={show ? t('auth.hidePassword') : t('auth.showPassword')}
              title={show ? t('auth.hidePassword') : t('auth.showPassword')}
              onClick={() => setShow(!show)}
            />
          </div>
        </Field>
        <Field label={t('auth.confirmPassword')} required error={errorFor('again')}>
          <Input
            type={show ? 'text' : 'password'}
            dir="ltr"
            autoComplete="new-password"
            value={form.again}
            onChange={set('again')}
          />
        </Field>
      </div>
    </FormPage>
  );
}
